import './DocumentUpload.css';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  ApplicationAttachmentViewModel,
  AttachmentInstructions,
  BaseResponse,
  DocumentUploadModel,
  UploadResponse,
} from './types';
import { ApiClient } from '#/Scripts/utilities/ApiClient';
import { truncateFileNameForDisplay } from '#/Scripts/utilities/FilenameTools';
import epayImg from '#images/epay.png';
import handShakeImg from '#images/handshake.png';
import Button from '#sign-up/components/common/Button/Button';
import { ButtonVariants } from '#sign-up/components/common/Button/buttonTypes';
import { Modal } from '#sign-up/components/Modal/Modal';
import { FileUpload } from '#sign-up/components/shared/FileUpload/FileUpload';
import { FileData } from '#sign-up/components/shared/FileUpload/FileUploadModels';
import '#sign-up/Signup.css';

interface DocumentUploadProps {
  model: DocumentUploadModel;
}

const getAttachmentInstructions = (attachment: ApplicationAttachmentViewModel | null): AttachmentInstructions => {
  if (!attachment) {
    return {};
  }

  const fieldName = attachment.FieldName.toLowerCase();
  const description = attachment.Description.toLowerCase();

  const isDriversLicenseFront = fieldName.includes('driver') && fieldName.includes('front');
  const isDriversLicenseBack = fieldName.includes('driver') && fieldName.includes('back');
  const isW9 =
    fieldName.includes('w-9') || fieldName.includes('w9') || description.includes('w-9') || description.includes('w9');
  const isBankStatementOrVoidedCheck =
    fieldName.includes('bank') ||
    fieldName.includes('voided check') ||
    description.includes('bank statement') ||
    description.includes('voided check');

  if (isDriversLicenseFront) {
    return {
      heading: "Scanned copy of the driver's license (Front)",
      bullets: ['Text clearly legible', 'Color image', 'Cropped to corners', 'No background', 'Not expired'],
    };
  }

  if (isDriversLicenseBack) {
    return {
      heading: "Scanned copy of the driver's license (Back)",
      bullets: ['Text clearly legible', 'Color image', 'Cropped to corners', 'No background', 'Not expired'],
    };
  }

  if (isW9) {
    return {
      heading: (
        <>
          <u>
            <strong>Signed</strong>
          </u>{' '}
          copy (dated{' '}
          <u>
            <strong>within the last year</strong>
          </u>
          ) of the W-9 for the account&apos;s legal entity
        </>
      ),
      subText: (
        <>
          An IRS template of this form can be found{' '}
          <a
            style={{ whiteSpace: 'nowrap', color: 'green' }}
            href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            here
          </a>
          .
        </>
      ),
      bullets: ['Legal business name', 'Address', 'Employer Identification Number (EIN)'],
    };
  }

  if (isBankStatementOrVoidedCheck) {
    return {
      heading: 'Bank Statement, Voided Check, or Bank Letter dated within the last 12 months',
      bullets: ['Account holder name', 'Bank account number', 'Bank name and logo', 'Date within the last year'],
    };
  }

  return {
    heading: attachment.Description || attachment.FieldName,
  };
};

const getRequestVerificationToken = (): string => {
  const tokenInput = document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]');
  return tokenInput?.value ?? '';
};

const getSafeNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

export const DocumentUpload = ({ model }: DocumentUploadProps) => {
  const attachments = model.ApplicationAttachmentViewModels ?? [];

  const [selectedAttachment, setSelectedAttachment] = useState<ApplicationAttachmentViewModel | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileList, setFileList] = useState<FileData>(new Map());
  const [fileNameOverrides, setFileNameOverrides] = useState<Record<string, string | null>>({});

  const uploadMutation = useMutation({
    mutationFn: async ({ uniqueId, file }: { uniqueId: string; file: File }) => {
      const token = getRequestVerificationToken();
      const formData = new FormData();
      formData.append('__RequestVerificationToken', token);
      formData.append(file.name, file);

      let response: UploadResponse;
      try {
        response = await ApiClient.postFormData(`/Home/Upload/${uniqueId}`, formData);
      } catch (error: unknown) {
        console.error('Failed to upload file:', error);
        throw new Error('Failed to upload file.');
      }

      if (response.Success !== true) {
        throw new Error(getSafeNonEmptyString(response.ErrorMessage) ?? 'Failed to upload file.');
      }

      return {
        uniqueId,
        fileName: getSafeNonEmptyString(response.FileName) ?? file.name,
      };
    },
    onSuccess: ({ uniqueId, fileName }) => {
      setFileNameOverrides((prev) => ({ ...prev, [uniqueId]: fileName }));

      // Remove focus from the Upload button to prevent hover color sticking
      setTimeout(() => {
        const uploadButton = document.activeElement as HTMLElement;
        if (uploadButton?.blur) {
          uploadButton.blur();
        }
      }, 0);

      // Close modal directly here — closeUploadModal guards against closing while isPending,
      // but onSuccess fires before React re-renders with isPending=false (stale closure).
      setIsUploadModalOpen(false);
      setSelectedAttachment(null);
      setFileList(new Map());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (uniqueId: string) => {
      const token = getRequestVerificationToken();
      const formData = new FormData();
      formData.append('__RequestVerificationToken', token);

      let response: BaseResponse;
      try {
        response = await ApiClient.postFormData(`/Home/Delete/${uniqueId}`, formData);
      } catch (error: unknown) {
        console.error('Failed to delete file:', error);
        throw new Error('Failed to delete file. Please try again.');
      }

      if (response.Success !== true) {
        throw new Error('Failed to delete file. Please try again.');
      }

      return { uniqueId };
    },
    onSuccess: ({ uniqueId }) => {
      setFileNameOverrides((prev) => ({ ...prev, [uniqueId]: null }));
    },
  });

  const isUploading = uploadMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const isBusy = isUploading || isDeleting;

  const uploadingAttachmentId = isUploading ? (uploadMutation.variables?.uniqueId ?? null) : null;
  const deletingAttachmentId = isDeleting ? deleteMutation.variables : null;

  const modalErrorMessage =
    uploadMutation.isError && uploadMutation.error instanceof Error ? uploadMutation.error.message : null;

  const pageErrorMessage =
    deleteMutation.isError && deleteMutation.error instanceof Error ? deleteMutation.error.message : null;

  const instructions = useMemo(() => getAttachmentInstructions(selectedAttachment), [selectedAttachment]);

  const handleFileListChange = (newFileList: FileData) => {
    setFileList(newFileList);
    if (newFileList.size === 0) {
      uploadMutation.reset();
    }
  };

  const getDisplayedFileName = (attachment: ApplicationAttachmentViewModel): string | null => {
    const fileName = Object.hasOwn(fileNameOverrides, attachment.UniqueId)
      ? fileNameOverrides[attachment.UniqueId]
      : (attachment.FileName ?? null);
    if (!fileName) return null;

    return truncateFileNameForDisplay(fileName);
  };

  const allDocumentsUploaded =
    attachments.length > 0 && attachments.every((attachment) => !!getDisplayedFileName(attachment));

  const openUploadModal = (attachment: ApplicationAttachmentViewModel) => {
    if (isBusy) return;

    setSelectedAttachment(attachment);
    setFileList(new Map());
    uploadMutation.reset();
    setIsUploadModalOpen(true);
  };

  const closeUploadModal = () => {
    if (isUploading) return;

    setIsUploadModalOpen(false);
    setSelectedAttachment(null);
    setFileList(new Map());
    uploadMutation.reset();
  };

  const handleUploadSubmit = () => {
    if (!selectedAttachment || fileList.size === 0 || isUploading) {
      return;
    }

    const selectedFile = [...fileList.values()][0]?.file;
    if (!selectedFile) return;

    uploadMutation.mutate({ uniqueId: selectedAttachment.UniqueId, file: selectedFile });
  };

  const handleDelete = (attachment: ApplicationAttachmentViewModel) => {
    if (isBusy) return;
    deleteMutation.mutate(attachment.UniqueId);
  };

  const hasSelectedFile = fileList.size > 0;

  return (
    <div className="signup">
      <div className="signup-container">
        <div className="signup-header">
          <img className="signup-epay-logo" src={epayImg} alt="ePayPolicy" />
          Account Application
        </div>

        <div className="signup-main">
          <div className="document-upload-container">
            <div className="signup-form">
              {allDocumentsUploaded && (
                <div className="completion-banner">
                  <img src={handShakeImg} alt="Signup complete" className="completion-banner-image" />

                  <div className="completion-banner-content">
                    <div className="completion-banner-title">Sign up complete</div>

                    <div className="completion-banner-subtitle">
                      We&apos;ll reach out to you when your application is processed.
                    </div>
                  </div>
                </div>
              )}

              <div className="signup-form-header">
                <h1>Documentation Upload</h1>
                <p>To complete your application, upload the required documentation via our secured page.</p>
              </div>

              {pageErrorMessage && <div className="error-message">{pageErrorMessage}</div>}

              <div className="attachments-list">
                {attachments.map((attachment) => {
                  const displayedFileName = getDisplayedFileName(attachment);
                  const isRowDeleting = deletingAttachmentId === attachment.UniqueId;
                  const isRowUploading = uploadingAttachmentId === attachment.UniqueId;

                  return (
                    <div key={attachment.UniqueId} className="attachment-item">
                      <div className="attachment-info">
                        <div className="attachment-field-name">{attachment.FieldName}</div>

                        <div className="attachment-description">{attachment.Description}</div>

                        {displayedFileName && (
                          <div
                            className="attachment-uploaded"
                            title={fileNameOverrides[attachment.UniqueId] || attachment.FileName || ''}
                          >
                            Uploaded: {displayedFileName}
                          </div>
                        )}
                      </div>

                      <div className="attachment-actions">
                        {displayedFileName ? (
                          <Button
                            type="button"
                            variant={ButtonVariants.outline}
                            disabled={isBusy}
                            onClick={() => handleDelete(attachment)}
                          >
                            {isRowDeleting ? 'Deleting...' : 'Delete'}
                          </Button>
                        ) : (
                          <Button type="button" disabled={isBusy} onClick={() => openUploadModal(attachment)}>
                            {isRowUploading ? 'Uploading...' : 'Upload'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {attachments.length === 0 && (
                  <div className="no-documents-message">No required documents were returned for this application.</div>
                )}
              </div>

              {allDocumentsUploaded && (
                <div className="all-uploaded-message">All required documents have been uploaded.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isUploadModalOpen}
        handleClose={isUploading ? () => undefined : closeUploadModal}
        title="Upload Document"
        shouldCloseOnOverlayClick={!isUploading}
        shouldCloseOnEsc={!isUploading}
        disableClose={isUploading}
        footer={
          <div className="modal-footer-actions">
            <Button type="button" variant={ButtonVariants.outline} onClick={closeUploadModal} disabled={isUploading}>
              Cancel
            </Button>
            <Button type="button" onClick={handleUploadSubmit} disabled={!hasSelectedFile || isUploading}>
              {isUploading ? 'Uploading...' : 'Submit'}
            </Button>
          </div>
        }
      >
        <div className="modal-content-wrapper">
          {modalErrorMessage && <div className="modal-error-message">{modalErrorMessage}</div>}

          {selectedAttachment && (
            <div>
              <div className="modal-attachment-field">{selectedAttachment.FieldName}</div>

              {instructions.heading && <p className="modal-instruction-text">{instructions.heading}</p>}

              {instructions.subText && <p className="modal-instruction-text">{instructions.subText}</p>}

              {instructions.bullets?.length ? (
                <div className="modal-requirements">
                  <p className="modal-requirements-title">Scan requirements:</p>
                  <ul className="modal-requirements-list">
                    {instructions.bullets.map((bullet: string) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}

          <FileUpload
            fileList={fileList}
            setFileList={handleFileListChange}
            maxFileUploadCount={1}
            acceptedFileTypes=".jpeg,.jpg,.png,.pdf"
            maxSize={10 * 1000 * 1000}
            disabled={isUploading}
          />
        </div>
      </Modal>
    </div>
  );
};
